// P5 T4 — notification inbox source of truth for the header bell + panel.
// Fetch-on-login, a 60s poll safety net, and a live refetch on the existing
// `document_notify` WS event (queue:<tenant> rail — the notify executor now
// carries the persisted notificationId). Modeled on useDirectedNotes.
import { useCallback, useEffect, useState } from "react";
import type { WsClient } from "@/lib/ws-client";
import { notificationsApi, type NotificationRecord } from "../api/client";

const POLL_MS = 60_000;

export function useNotifications(wsClient: WsClient | null): {
  notifications: NotificationRecord[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refetch: () => Promise<void>;
} {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);

  const refetch = useCallback(async () => {
    try {
      const res = await notificationsApi.list({ limit: 50 });
      setNotifications(res.notifications);
    } catch {
      // best-effort — the bell simply stays at its last known state
    }
  }, []);

  useEffect(() => {
    void refetch();
    const t = setInterval(() => void refetch(), POLL_MS);
    return () => clearInterval(t);
  }, [refetch]);

  useEffect(() => {
    if (!wsClient) return;
    const off = wsClient.onPayload("document_notify", () => {
      void refetch();
    });
    return off;
  }, [wsClient, refetch]);

  const markRead = useCallback(async (id: string) => {
    await notificationsApi.markRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id && n.readAt === null ? { ...n, readAt: new Date().toISOString() } : n)),
    );
  }, []);

  const markAllRead = useCallback(async () => {
    await notificationsApi.markAllRead();
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => (n.readAt === null ? { ...n, readAt: now } : n)));
  }, []);

  return {
    notifications,
    unreadCount: notifications.filter((n) => n.readAt === null).length,
    markRead,
    markAllRead,
    refetch,
  };
}
